import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Spinner, Alert, Table, Modal } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

const DELIVERY_STEPS = [
    { key: "Pending", label: "Pending", icon: "📦" },
    { key: "Shipped", label: "Shipped", icon: "🚚" },
    { key: "Delivered", label: "Delivered", icon: "✅" }
];

function DeliveryStatusStepper({ status }) {
    const isReturned = status === "Returned";
    const activeIndex = isReturned ? -1 : DELIVERY_STEPS.findIndex((s) => s.key === status);

    return (
        <div className="d-flex align-items-center px-2 py-3">
            {DELIVERY_STEPS.map((step, index) => {
                const reached = !isReturned && index <= activeIndex;
                const lineFilled = !isReturned && index < activeIndex;

                return (
                    <React.Fragment key={step.key}>
                        <div className="d-flex flex-column align-items-center" style={{ minWidth: "70px" }}>
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    fontSize: "18px",
                                    backgroundColor: reached ? "#2b77c0" : "#e9ecef",
                                    color: reached ? "#ffffff" : "#495057",
                                    border: reached ? "2px solid #2b77c0" : "2px solid #ced4da"
                                }}
                            >
                                {step.icon}
                            </div>
                            <span
                                className="mt-2 small fw-semibold text-center"
                                style={{ color: reached ? "#2b77c0" : "#495057" }}
                            >
                                {step.label}
                            </span>
                        </div>

                        {index < DELIVERY_STEPS.length - 1 && (
                            <div
                                style={{
                                    flex: 1,
                                    height: "3px",
                                    backgroundColor: lineFilled ? "#2b77c0" : "#ced4da",
                                    marginBottom: "22px"
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}

            {isReturned && (
                <div className="d-flex flex-column align-items-center ms-3" style={{ minWidth: "70px" }}>
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{ width: "40px", height: "40px", fontSize: "18px", backgroundColor: "#dc3545", color: "#ffffff", border: "2px solid #dc3545" }}
                    >
                        ↩️
                    </div>
                    <span className="mt-2 small fw-semibold text-center" style={{ color: "#dc3545" }}>Returned</span>
                </div>
            )}
        </div>
    );
}

function ProductThumb({ src, alt, size = 44, onClick }) {
    const [failed, setFailed] = useState(false);

    const fallback =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>
                <rect width='100%' height='100%' fill='#e9ecef'/>
                <text x='50%' y='50%' font-size='8' fill='#6c757d' text-anchor='middle' dominant-baseline='middle'>No Img</text>
             </svg>`
        );

    return (
        <img
            src={failed || !src ? fallback : src}
            alt={alt}
            style={{ width: `${size}px`, height: `${size}px`, objectFit: "contain", flexShrink: 0, cursor: src && !failed ? "pointer" : "default" }}
            onError={() => setFailed(true)}
            onClick={() => src && !failed && onClick && onClick(src)}
        />
    );
}

export default function WebOrderEditPage() {
    const { id } = useParams();
    const { setSidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const [order, setOrder] = useState(null);
    const [couriers, setCouriers] = useState([]);
    const [products, setProducts] = useState([]);

    const [formData, setFormData] = useState({
        name: "", email: "", phone: "", vat_id: "",
        country: "", city: "", address: "",
        payment_status: "Pending", payment_date: "", payment_message: "",
        courier_company_id: "", tracking_number: "", delivery_status: "Pending", delivery_days: "",
        image_1: null,
        image_2: null
    });

    const [imagePreviews, setImagePreviews] = useState({
        image_1: null,
        image_2: null
    });

    const [previewModal, setPreviewModal] = useState({
        show: false,
        imgUrl: "",
        title: ""
    });

    const fetchOrder = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [orderRes, courierRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/admin/web-orders/${id}`, { headers: authHeaders() }),
                axios.get(`${process.env.REACT_APP_API_URL}/admin/web-orders/initial-data`, { headers: authHeaders() })
            ]);

            if (orderRes.data?.success) {
                const o = orderRes.data.data;
                setOrder(o);

                setFormData({
                    name: o.buyer?.name || "",
                    email: o.buyer?.email || "",
                    phone: o.buyer?.phone || "",
                    vat_id: o.buyer?.vat_id || "",
                    country: o.address?.country || "",
                    city: o.address?.city || "",
                    address: o.address?.address || "",
                    payment_status: o.payment?.status || "Pending",
                    payment_date: o.payment?.date ? o.payment.date.split("T")[0] : "",
                    payment_message: o.payment?.message || "",
                    courier_company_id: o.delivery?.courier_company?.id || o.delivery?.courier_company_id || "",
                    tracking_number: o.delivery?.tracking_number || "",
                    delivery_status: o.delivery?.status || "Pending",
                    delivery_days: o.delivery?.delivery_days || "",
                    image_1: null,
                    image_2: null
                });

                setImagePreviews({
                    image_1: o.image_1 ? `${process.env.REACT_APP_IMG_URL}${o.image_1}` : null,
                    image_2: o.image_2 ? `${process.env.REACT_APP_IMG_URL}${o.image_2}` : null
                });

                setProducts(
                    (o.products || []).map((p) => ({
                        id: p.id,
                        title: p.product?.title || `Product #${p.product_id}`,
                        card_img: p.product?.card_img || null,
                        quantity: p.quantity || 1,
                        unit_price: p.unit_price ?? "",
                        shipping: p.shipping ?? "",
                        net_weight: p.net_weight ?? "",
                        declared_value: p.declared_value ?? "",
                        description: p.description || ""
                    }))
                );
            } else {
                setError("Order details not found.");
            }

            // Extracting courier_companies from initialData endpoint key
            const courierList = courierRes.data?.courier_companies || courierRes.data || [];
            setCouriers(courierList);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch order details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOrder();
        setSidebar(false);
    }, [fetchOrder]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (field, file) => {
        if (file) {
            setFormData((prev) => ({ ...prev, [field]: file }));
            setImagePreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
        }
    };

    const handleProductChange = (index, field, value) => {
        setProducts((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
    };

    const openImageZoom = (url, title = "Image View") => {
        if (url) {
            setPreviewModal({ show: true, imgUrl: url, title });
        }
    };

    const getCourierName = (c) => {
        return c.company_name || c.name || c.title || `Courier #${c.id}`;
    };

    const totals = products.reduce(
        (acc, p) => {
            acc.subtotal += Number(p.unit_price) || 0;
            acc.shipping += Number(p.shipping) || 0;
            return acc;
        },
        { subtotal: 0, shipping: 0 }
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const sendFormData = new FormData();

            Object.keys(formData).forEach((key) => {
                if (key !== "image_1" && key !== "image_2") {
                    if (formData[key] !== "" && formData[key] !== null) {
                        sendFormData.append(key, formData[key]);
                    }
                }
            });

            if (formData.image_1 instanceof File) {
                sendFormData.append("image_1", formData.image_1);
            }
            if (formData.image_2 instanceof File) {
                sendFormData.append("image_2", formData.image_2);
            }

            sendFormData.append("_method", "PUT");

            products.forEach((p, index) => {
                sendFormData.append(`products[${index}][id]`, p.id);
                if (p.unit_price !== "") sendFormData.append(`products[${index}][unit_price]`, p.unit_price);
                if (p.shipping !== "") sendFormData.append(`products[${index}][shipping]`, p.shipping);
                if (p.net_weight !== "") sendFormData.append(`products[${index}][net_weight]`, p.net_weight);
                if (p.declared_value !== "") sendFormData.append(`products[${index}][declared_value]`, p.declared_value);
                if (p.description !== "") sendFormData.append(`products[${index}][description]`, p.description);
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/web-orders/${id}`,
                sendFormData,
                {
                    headers: {
                        ...authHeaders(),
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (response.data?.success) {
                setSuccessMsg("Website order updated successfully!");
                setOrder(response.data.data);
            }
        } catch (err) {
            if (err.response?.data?.errors) {
                const errMsgs = Object.values(err.response.data.errors).flat().join(" ");
                setError(errMsgs);
            } else {
                setError(err.response?.data?.message || err.response?.data?.error || "Failed to update website order.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout hideSidebar={true}>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading order information...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout hideSidebar={true}>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Edit Web Order #{order?.order_number || id}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home") || "Home"}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/admin/web-orders" className="mc-breadcrumb-link">Web Orders</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Edit Order</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <div
                    className="px-3 py-2 rounded-3 border d-flex align-items-center gap-2 mb-3"
                    style={{ backgroundColor: "#fff8e6", borderColor: "#ffe8a1" }}
                >
                    <span className="small text-muted fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Order Source:</span>
                    <span className="badge bg-warning text-dark fw-bold px-2 py-1" style={{ fontSize: "13px" }}>
                        🛒 {order?.marketplace?.name || order?.marketplace?.title || "Website"}
                    </span>
                </div>

                <Col xl={12}>
                    <Card className="p-3 mb-4 border-0 shadow-sm">
                        <DeliveryStatusStepper status={formData.delivery_status} />
                    </Card>
                </Col>

                {error && <Col xl={12} className="mb-3"><Alert variant="danger">{error}</Alert></Col>}
                {successMsg && <Col xl={12} className="mb-3"><Alert variant="success">{successMsg}</Alert></Col>}

                <Col xl={12}>
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            {/* Attachments Section */}
                            <Col lg={4}>
                                <Card className="p-4 mb-4 border-0 shadow-sm">
                                    <h5 className="fw-bold mb-3 text-dark">Order Attachments</h5>

                                    <div className="mb-4">
                                        <Form.Label className="fw-semibold small">Image 1</Form.Label>
                                        <div
                                            className="border rounded p-2 text-center mb-2 bg-light position-relative"
                                            style={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}
                                        >
                                            {imagePreviews.image_1 ? (
                                                <img
                                                    src={imagePreviews.image_1}
                                                    alt="Image 1"
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "160px", objectFit: "contain", cursor: "pointer", transition: "transform 0.2s" }}
                                                    onClick={() => openImageZoom(imagePreviews.image_1, "Attachment Image 1")}
                                                    title="Click to Enlarge"
                                                />
                                            ) : (
                                                <span className="text-muted small">No Image Uploaded</span>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            accept="image/png, image/jpeg, image/jpg, image/webp"
                                            onChange={(e) => handleImageChange("image_1", e.target.files[0])}
                                        />
                                    </div>

                                    <div>
                                        <Form.Label className="fw-semibold small">Image 2</Form.Label>
                                        <div
                                            className="border rounded p-2 text-center mb-2 bg-light position-relative"
                                            style={{ minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}
                                        >
                                            {imagePreviews.image_2 ? (
                                                <img
                                                    src={imagePreviews.image_2}
                                                    alt="Image 2"
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "160px", objectFit: "contain", cursor: "pointer", transition: "transform 0.2s" }}
                                                    onClick={() => openImageZoom(imagePreviews.image_2, "Attachment Image 2")}
                                                    title="Click to Enlarge"
                                                />
                                            ) : (
                                                <span className="text-muted small">No Image Uploaded</span>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            accept="image/png, image/jpeg, image/jpg, image/webp"
                                            onChange={(e) => handleImageChange("image_2", e.target.files[0])}
                                        />
                                    </div>
                                </Card>
                            </Col>

                            {/* Buyer Details & Shipping */}
                            <Col lg={5}>
                                <Card className="p-4 mb-4 border-0 shadow-sm">
                                    <h5 className="fw-bold mb-3 text-dark">Buyer & Shipping Address</h5>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Name</Form.Label>
                                                <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Buyer name" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Email</Form.Label>
                                                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Phone</Form.Label>
                                                <Form.Control type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">VAT ID</Form.Label>
                                                <Form.Control type="text" name="vat_id" value={formData.vat_id} onChange={handleChange} placeholder="VAT ID" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Country</Form.Label>
                                                <Form.Control type="text" name="country" value={formData.country} onChange={handleChange} placeholder="Country" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">City</Form.Label>
                                                <Form.Control type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Full Address</Form.Label>
                                                <Form.Control as="textarea" rows={3} name="address" value={formData.address} onChange={handleChange} placeholder="Detailed address line" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card>

                                <Card className="p-4 border-0 shadow-sm">
                                    <h5 className="fw-bold mb-3 text-dark">Delivery & Courier Setup</h5>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Delivery Status</Form.Label>
                                                <Form.Select name="delivery_status" value={formData.delivery_status} onChange={handleChange}>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Shipped">Shipped</option>
                                                    <option value="Delivered">Delivered</option>
                                                    <option value="Returned">Returned</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Courier Company</Form.Label>
                                                <Form.Select name="courier_company_id" value={formData.courier_company_id} onChange={handleChange}>
                                                    <option value="">Select courier</option>
                                                    {couriers.map((c) => (
                                                        <option key={c.id} value={c.id}>
                                                            {getCourierName(c)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Tracking Number</Form.Label>
                                                <Form.Control type="text" name="tracking_number" value={formData.tracking_number} onChange={handleChange} placeholder="Tracking Code" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Delivery Days</Form.Label>
                                                <Form.Control type="number" name="delivery_days" value={formData.delivery_days} onChange={handleChange} placeholder="Est. Days" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            {/* Payment Info */}
                            <Col lg={3}>
                                <Card className="p-4 mb-4 border-0 shadow-sm">
                                    <h5 className="fw-bold mb-3 text-dark">Payment Details</h5>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Payment Status</Form.Label>
                                                <Form.Select name="payment_status" value={formData.payment_status} onChange={handleChange}>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Paid">Paid</option>
                                                    <option value="Refunded">Refunded</option>
                                                    <option value="Cancelled">Cancelled</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Payment Date</Form.Label>
                                                <Form.Control type="date" name="payment_date" value={formData.payment_date} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold small">Payment Message</Form.Label>
                                                <Form.Control as="textarea" rows={2} name="payment_message" value={formData.payment_message} onChange={handleChange} placeholder="Transaction notes" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card>

                                <Card className="p-4 border-0 shadow-sm">
                                    <h5 className="fw-bold mb-3 text-dark">Order Summary</h5>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Subtotal</span>
                                        <span className="fw-semibold">{(order?.subtotal ?? totals.subtotal).toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Shipping</span>
                                        <span className="fw-semibold">{(order?.shipping_charges ?? totals.shipping).toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted">Grand Total</span>
                                        <span className="fw-bold">{(order?.grand_total ?? totals.subtotal + totals.shipping).toLocaleString()}</span>
                                    </div>
                                </Card>

                                <div className="d-flex gap-2 mt-4">
                                    <Button type="submit" variant="primary" className="w-100 fw-semibold" disabled={submitting} style={{ backgroundColor: "#2b77c0", borderColor: "#2b77c0" }}>
                                        {submitting ? "SAVING..." : "UPDATE ORDER"}
                                    </Button>
                                    <Button type="button" variant="light" className="border fw-semibold" onClick={() => navigate("/admin/web-orders")}>
                                        CANCEL
                                    </Button>
                                </div>
                            </Col>

                            {/* Products Table */}
                            {products.length > 0 && (
                                <Col xl={12}>
                                    <Card className="p-4 border-0 shadow-sm">
                                        <h5 className="fw-bold mb-3 text-dark">Ordered Products ({products.length})</h5>
                                        <div style={{ overflowX: "auto" }}>
                                            <Table bordered className="align-middle mb-0" style={{ minWidth: "900px" }}>
                                                <thead>
                                                    <tr className="text-uppercase small text-muted">
                                                        <th style={{ width: "26%" }}>Product</th>
                                                        <th style={{ width: "14%" }}>Unit Price</th>
                                                        <th style={{ width: "14%" }}>Shipping</th>
                                                        <th style={{ width: "14%" }}>Net Weight</th>
                                                        <th style={{ width: "14%" }}>Declared Value</th>
                                                        <th style={{ width: "18%" }}>Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {products.map((p, index) => (
                                                        <tr key={p.id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <ProductThumb
                                                                        src={p.card_img ? `${process.env.REACT_APP_IMG_URL}${p.card_img}` : null}
                                                                        alt={p.title}
                                                                        size={44}
                                                                        onClick={(url) => openImageZoom(url, p.title)}
                                                                    />
                                                                    <div>
                                                                        <div className="fw-semibold">{p.title}</div>
                                                                        <small className="text-muted">Qty: {p.quantity}</small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Form.Control type="number" value={p.unit_price} onChange={(e) => handleProductChange(index, "unit_price", e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <Form.Control type="number" value={p.shipping} onChange={(e) => handleProductChange(index, "shipping", e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <Form.Control type="number" value={p.net_weight} onChange={(e) => handleProductChange(index, "net_weight", e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <Form.Control type="number" value={p.declared_value} onChange={(e) => handleProductChange(index, "declared_value", e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <Form.Control type="text" value={p.description} onChange={(e) => handleProductChange(index, "description", e.target.value)} />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <div className="d-flex justify-content-end gap-4 mt-3 fw-semibold">
                                            <span>Subtotal: {totals.subtotal.toLocaleString()}</span>
                                            <span>Shipping: {totals.shipping.toLocaleString()}</span>
                                            <span>Grand Total: {(totals.subtotal + totals.shipping).toLocaleString()}</span>
                                        </div>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    </Form>
                </Col>
            </Row>

            {/* Lightbox / Zoom Preview Modal */}
            <Modal
                show={previewModal.show}
                onHide={() => setPreviewModal({ show: false, imgUrl: "", title: "" })}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">{previewModal.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3 bg-dark rounded-bottom">
                    <img
                        src={previewModal.imgUrl}
                        alt="Enlarged Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: "80vh", objectFit: "contain" }}
                    />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}